import { Add, Delete, Edit, UploadFile } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate } from '@reach/router';
import React, { useEffect, useState, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import AsyncTableSearch from '../components/AsyncTableSearch';
import ExcelImportDialog from '../components/ExcelImportDialog';
import Layout from '../components/Layout';
import SchemaEditor from '../components/SchemaEditor';
import StoragePickerDialog from '../components/StoragePickerDialog';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useClients, useUI } from '../store';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const ClientsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const {
    clients,
    loading,
    error,
    pagination,
    filters,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    setClientsFilters,
    setClientsPage
  } = useClients();

  const { startEditing, stopEditing, addNotification } = useUI();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();

  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [clientSchema, setClientSchema] = useState([]);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [presignedMap, setPresignedMap] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerImagesOnly, setPickerImagesOnly] = useState(false);
  const [pickerTargetField, setPickerTargetField] = useState(null);
  // Состояние для пагинации поиска
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });

  const baseFilterFields = [
    { value: 'name', label: 'Имя' },
    { value: 'phone', label: 'Телефон' },
    { value: 'email', label: 'Email' },
    { value: 'address', label: 'Адрес' },
  ];

  const allFilterFields = useMemo(() => [
    ...baseFilterFields,
    ...clientSchema.map(f => ({ value: f.name, label: f.name }))
  ], [clientSchema]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchClients();
      fetchSchema();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Фильтрация клиентов локально (только при неактивном серверном поиске)
  useEffect(() => {
    // Если активен серверный поиск - не делаем локальную фильтрацию
    if (searchActive) return;

    if (clients.length > 0) {
      let filtered = clients;

      // Применяем расширенные фильтры (локально)
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(client => {
          return advancedFilters.every(filter => {
            const value = client[filter.field];
            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [clients, advancedFilters, clientSchema, searchActive]);

  const fetchSchema = async () => {
    try {
      const resp = await apiClient.get('/clients/schema');
      setClientSchema(resp.data.schema || []);
    } catch (err) {
      console.error('Не удалось получить схему клиентов', err);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      const clientId = client.id || client._id;
      startEditing(clientId);

      const dynamicVals = {};
      clientSchema.forEach(f => {
        dynamicVals[f.name] = client[f.name] ?? '';
      });
      setFormData({
        name: client.name,
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        ...dynamicVals
      });
    } else {
      setEditingClient(null);
      const dynamicDefaults = {};
      clientSchema.forEach(f => {
        dynamicDefaults[f.name] = f.default ?? '';
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        ...dynamicDefaults
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (editingClient) {
      stopEditing(editingClient.id || editingClient._id);
    }
    setOpenDialog(false);
    setEditingClient(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient.id || editingClient._id, formData);
        addNotification('success', 'Клиент обновлен');
        stopEditing(editingClient.id || editingClient._id);
      } else {
        await createClient(formData);
        addNotification('success', 'Клиент создан');
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Ошибка при сохранении клиента:', err);
      addNotification('error', err.response?.data?.message || 'Не удалось сохранить данные клиента');
      if (editingClient) {
        stopEditing(editingClient.id || editingClient._id);
      }
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Вы уверены, что хотите удалить этого клиента?')) {
      try {
        await deleteClient(clientId);
        addNotification('success', 'Клиент удален');
      } catch (err) {
        console.error('Ошибка при удалении клиента:', err);
        addNotification('error', err.response?.data?.message || 'Не удалось удалить клиента');
      }
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters([]);
  };

  const handlePageChange = (event, value) => {
    if (searchActive) {
      // При активном поиске меняем страницу поиска
      setSearchPage(value);
    } else {
      // Обычная пагинация
      setClientsPage(value);
    }
  };

  // Callback для результатов поиска
  const handleSearchResults = (items) => {
    setFilteredClients(items);
  };

  // Callback для изменения пагинации поиска
  const handleSearchPaginationChange = (paginationInfo) => {
    setSearchPagination(paginationInfo);
  };

  // Callback для изменения активности поиска
  const handleSearchActiveChange = (active) => {
    setSearchActive(active);
    if (!active) {
      // Сбрасываем на основные данные при очистке поиска
      setFilteredClients(clients);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  };

  const uploadForField = async (fieldName, fileObj) => {
    try {
      const form = new FormData();
      form.append('file', fileObj);
      form.append('prefix', `clients/`);
      const resp = await apiClient.post('/storage/files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const key = resp.data?.key;
      if (key) {
        setFormData(prev => ({ ...prev, [fieldName]: key }));
      }
    } catch (e) {
      console.error('Ошибка загрузки файла', e);
      addNotification('error', 'Ошибка загрузки файла');
    }
  };

  const fetchPresigned = async (key) => {
    if (!key || presignedMap[key]) return presignedMap[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp.data?.url;
      if (url) {
        setPresignedMap(prev => ({ ...prev, [key]: url }));
        return url;
      }
    } catch (e) {
      console.error('Ошибка получения ссылки на файл', e);
    }
    return null;
  };

  const renderFileField = (field, isImage) => {
    const key = formData[field.name];
    const fileName = key ? String(key).split('/').pop() : '';
    return (
      <Box>
        {key ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isImage && (
              <Box sx={{ width: 120, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {presignedMap[key] ? (
                  <img src={presignedMap[key]} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                ) : (
                  <Button size="small" variant="outlined" onClick={async () => { await fetchPresigned(key); }}>
                    Показать превью
                  </Button>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{fileName}</Typography>
              <Button size="small" onClick={async () => {
                const url = await fetchPresigned(key);
                if (url) window.open(url, '_blank');
              }}>Открыть</Button>
            </Box>
            <Button variant="outlined" size="small" component="label">
              Заменить файл
              <input type="file" hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
            </Button>
            <Button variant="text" size="small" onClick={() => { setPickerTargetField(field.name); setPickerImagesOnly(!!isImage); setPickerOpen(true); }}>
              Выбрать из хранилища
            </Button>
          </Box>
        ) : (
          <Button variant="outlined" size="small" component="label">
            Загрузить {isImage ? 'изображение' : 'файл'}
            <input type="file" accept={isImage ? 'image/*' : undefined} hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
          </Button>
        )}
        {!key && (
          <Button sx={{ ml: 2 }} variant="text" size="small" onClick={() => { setPickerTargetField(field.name); setPickerImagesOnly(!!isImage); setPickerOpen(true); }}>
            Выбрать из хранилища
          </Button>
        )}
      </Box>
    );
  };

  // Виртуализированный рендеринг строк таблицы
  const Row = ({ index, style }) => {
    const client = filteredClients[index];
    const isOptimistic = client._optimistic;

    return (
      <TableRow
        key={client.id || client._id}
        style={{
          ...style,
          opacity: isOptimistic ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <TableCell sx={{ color: 'white' }}>
          {client.name}
          {isOptimistic && (
            <Chip
              label="..."
              size="small"
              sx={{ ml: 1, backgroundColor: '#FFC107', color: 'white', height: 18 }}
            />
          )}
        </TableCell>
        <TableCell sx={{ color: 'white' }}>{client.email || '—'}</TableCell>
        <TableCell sx={{ color: 'white' }}>{client.phone || '—'}</TableCell>
        <TableCell sx={{ color: 'white' }}>{client.address || '—'}</TableCell>
        {clientSchema.map((field) => (
          <TableCell key={field.name} sx={{ color: 'white' }}>
            {field.type === 'boolean' ? (
              client[field.name] ? 'Да' : 'Нет'
            ) : field.type === 'image' ? (
              client[field.name] ? (
                <img
                  alt={field.name}
                  src={presignedMap[client[field.name]] || ''}
                  style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }}
                  onMouseEnter={async () => { if (!presignedMap[client[field.name]]) await fetchPresigned(client[field.name]); }}
                />
              ) : '—'
            ) : field.type === 'file' ? (
              client[field.name] ? (
                <Link
                  component="button"
                  variant="body2"
                  onClick={async () => {
                    const url = await fetchPresigned(client[field.name]);
                    if (url) window.open(url, '_blank');
                  }}
                >
                  {String(client[field.name]).split('/').pop()}
                </Link>
              ) : '—'
            ) : (
              client[field.name] ?? '—'
            )}
          </TableCell>
        ))}
        <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
          <IconButton onClick={() => handleOpenDialog(client)}>
            <Edit />
          </IconButton>
          <IconButton onClick={() => handleDelete(client.id || client._id)}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  const useVirtualization = filteredClients.length > 100;

  return (
    <Layout>
      <Box sx={{
        p: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
      }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ mr: { sm: 'auto' } }}>
            Клиенты
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить клиента
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => setImportOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Импорт из Excel
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <AsyncTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            placeholder="Поиск клиентов..."
            searchDoc="clients"
            searchFields={["name", "email", "phone", "address"]}
            page={searchPage}
            perPage={pagination.perPage}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={allFilterFields}
          />
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Имя</TableCell>
                <TableCell sx={{ color: 'white' }}>Email</TableCell>
                <TableCell sx={{ color: 'white' }}>Телефон</TableCell>
                <TableCell sx={{ color: 'white' }}>Адрес</TableCell>
                {clientSchema.map((f) => (
                  <TableCell key={f.name} sx={{ color: 'white' }}>{f.name}</TableCell>
                ))}
                <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={5 + clientSchema.length} align="center">Загрузка...</TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={5 + clientSchema.length} align="center">
                    {searchQuery ? 'Клиенты не найдены' : 'Клиенты не найдены'}
                  </TableCell>
                </TableRow>
              ) : useVirtualization ? (
                <List
                  height={600}
                  itemCount={filteredClients.length}
                  itemSize={60}
                  width="100%"
                >
                  {Row}
                </List>
              ) : (
                filteredClients.map((client, index) => <Row key={client.id || client._id} index={index} style={{}} />)
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {/* Пагинация: разные данные для поиска и обычного режима */}
        {(() => {
          const currentPage = searchActive ? searchPagination.page : pagination.page;
          const currentTotalPages = searchActive ? searchPagination.pages : pagination.totalPages;
          const currentTotal = searchActive ? searchPagination.total : pagination.total;

          if (currentTotalPages <= 1 && !searchActive) return null;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
              {searchActive && (
                <Typography variant="body2" color="text.secondary">
                  Найдено: {currentTotal}
                </Typography>
              )}
              {currentTotalPages > 1 && (
                <Pagination 
                  color="primary" 
                  count={currentTotalPages} 
                  page={currentPage} 
                  onChange={handlePageChange} 
                />
              )}
            </Box>
          );
        })()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingClient ? 'Редактировать клиента' : 'Добавить клиента'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Имя"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Телефон"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Адрес"
              name="address"
              value={formData.address}
              onChange={handleChange}
              margin="normal"
            />
            {clientSchema.map((field) => (
              field.type === 'select' ? (
                <FormControl fullWidth margin="normal" key={field.name}>
                  <InputLabel id={`label-${field.name}`}>{field.name}</InputLabel>
                  <Select
                    labelId={`label-${field.name}`}
                    name={field.name}
                    value={formData[field.name] ?? ''}
                    label={field.name}
                    onChange={handleChange}
                    MenuProps={{
                      PaperProps: { style: { width: '100%' } }
                    }}
                  >
                    {(field.options || []).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field.type === 'file' ? (
                <Box key={field.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
                  {renderFileField(field, false)}
                </Box>
              ) : field.type === 'image' ? (
                <Box key={field.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
                  {renderFileField(field, true)}
                </Box>
              ) : (
                <TextField
                  key={field.name}
                  fullWidth
                  label={field.name}
                  name={field.name}
                  value={formData[field.name] ?? ''}
                  onChange={handleChange}
                  margin="normal"
                />
              )
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Button variant="outlined" onClick={() => setSchemaDialogOpen(true)} sx={{ ml: 2 }}>
        Изменить схему
      </Button>

      <Dialog open={schemaDialogOpen} onClose={() => setSchemaDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Редактирование схемы клиентов</DialogTitle>
        <DialogContent>
          <SchemaEditor schema={clientSchema} onChange={setClientSchema} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={async () => {
            try {
              await apiClient.put('/clients/schema', { schema: clientSchema });
              setSchemaDialogOpen(false);
              fetchClients();
            } catch (err) {
              console.error('Не удалось сохранить схему', err);
            }
          }}>Сохранить</Button>
        </DialogActions>
      </Dialog>

      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/clients/import"
        onSuccess={fetchClients}
      />

      <StoragePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(key) => { if (pickerTargetField && key) setFormData(prev => ({ ...prev, [pickerTargetField]: key })); setPickerOpen(false); }}
        imagesOnly={pickerImagesOnly}
        initialPrefix={`clients/`}
      />
    </Layout>
  );
};

export default ClientsPage;